import { useSyncExternalStore } from "react";

/**
 * Recently-opened records (issues/projects) for global-search ranking.
 *
 * A tiny reactive singleton: the issue-view provider calls `pushRecentRecord`
 * whenever an issue opens (from a card, deep link, or search), and the search
 * controller reads `useRecentRecords()` to float recent matches to the top.
 * Backed by localStorage so it survives reloads.
 *
 * usePersistedState isn't used here because it doesn't sync across separate
 * hook instances — the writer (provider) and reader (search) need to see the
 * same list reactively, which useSyncExternalStore over a module store gives.
 */
const STORAGE_KEY = "global-search:recent-issues";
const CAP = 20;

function load(): string[] {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

let recent: string[] = load();
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

export function pushRecentRecord(issueId: string | null | undefined) {
  if (!issueId) return;
  if (recent[0] === issueId) return; // already most-recent — no churn
  recent = [issueId, ...recent.filter((id) => id !== issueId)].slice(0, CAP);
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(recent));
  } catch {
    // ignore quota / unavailable storage
  }
  emit();
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot() {
  return recent;
}

/** Most-recently-opened issue ids, newest first. */
export function useRecentRecords(): string[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
